// Central business + content data for Aims Electrical
export const SITE_URL = "https://www.aims-electrical.co.uk";

export const BUSINESS = {
  name: "Aims Electrical",
  logo: "/aims-logo-v4.png",
  logoDark: "/aims-logo-dark-v5.png",
  owner: "Imaad",
  phoneDisplay: "07907 917 551",
  phoneHref: "tel:+447907917551",
  whatsappHref: "https://wa.me/447907917551",
  email: "aims.electrical78@gmail.com",
  emailHref: "mailto:aims.electrical78@gmail.com",
  address: "Hockney Road Industrial Estate, Bradford, BD8 9HQ",
  region: "Bradford & West Yorkshire",
  yearsInBusiness: 12,
  jobsCompleted: "750+",
  ratingValue: "5.0",
  reviewCount: 40,
  socials: {
    instagram: "https://www.instagram.com/aimselectricalltd/",
    tiktok: "https://www.tiktok.com/@aims.electrical",
    yell: "https://www.yell.com/biz/aims-electrical-bradford-10848300/",
  },
  hours: [
    { day: "Monday – Sunday", time: "24 hours" },
    { day: "Open all year", time: "365 days" },
  ],
};

// LocalBusiness (Electrician) JSON-LD — injected into <head> of every page.
export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Electrician",
  name: "Aims Electrical",
  telephone: "+447907917551",
  email: "aims.electrical78@gmail.com",
  priceRange: "££",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Hockney Road Industrial Estate",
    addressLocality: "Bradford",
    postalCode: "BD8 9HQ",
    addressCountry: "GB",
  },
  areaServed: [
    "Bradford", "Leeds", "Keighley", "Shipley", "Huddersfield",
    "Halifax", "Wakefield", "Bingley", "Ilkley", "Otley", "Pudsey", "Cleckheaton",
  ],
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"], opens: "00:00", closes: "23:59" },
  ],
};

const img = (base, w = 900) => `${base}&w=${w}&q=72&auto=format`;

export const SERVICES = [
  {
    id: "rewires",
    slug: "rewires",
    num: "01",
    title: "Rewires",
    blurb:
      "Full and partial house rewires carried out to current wiring regulations — safer circuits, minimal mess, certified on completion.",
    image: img("https://images.unsplash.com/photo-1758101755915-462eddc23f57?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0", 1100),
    imageAlt: "Electrician rewiring a domestic property in Bradford",
    seoTitle: "House Rewiring in Bradford & West Yorkshire | Aims Electrical",
    seoDescription:
      "Full and partial house rewires to current wiring regulations. Safer circuits, minimal mess, certified on completion. Covering Bradford, Leeds & West Yorkshire.",
    h1: "House Rewiring in Bradford & West Yorkshire",
    intro:
      "A full or partial rewire is one of the biggest electrical jobs a home will ever need — and one where getting it right really matters. Aims Electrical carries out house rewires across Bradford, Leeds, Huddersfield and the wider West Yorkshire area, all to the current wiring regulations (BS 7671), with minimal mess and full certification on completion.",
    included: [
      "Full and partial house rewires",
      "New consumer units and fuse boards",
      "Extra sockets, switches and lighting points",
      "Rewiring for extensions and renovations",
      "Safe removal of old, unsafe wiring",
      "Electrical Installation Certificate on completion",
    ],
    why:
      "As a trusted electrician in Bradford, we treat your home with respect — dust sheets down, cables run neatly, and everything tested and signed off before we leave. Whether you've bought a period property that needs a full rewire or just need a few extra circuits, we'll give you honest advice and a clear, fixed quote.",
  },
  {
    id: "cctv",
    slug: "cctv-installation",
    num: "02",
    title: "CCTV Systems",
    blurb:
      "Supply, install and configure HD CCTV for homes and businesses, with remote viewing set up on your phone.",
    image: img("https://images.unsplash.com/photo-1589935447067-5531094415d1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0", 1100),
    imageAlt: "HD CCTV camera installed on a building exterior in West Yorkshire",
    seoTitle: "CCTV Installation Bradford & West Yorkshire | Aims Electrical",
    seoDescription:
      "HD CCTV supply, installation and setup for homes and businesses, with remote viewing on your phone. Serving Bradford, Leeds, Huddersfield and the wider region.",
    h1: "CCTV Installation in Bradford & West Yorkshire",
    intro:
      "Protect your home or business with a professionally installed HD CCTV system. Aims Electrical supplies, installs and sets up CCTV across Bradford, Leeds, Huddersfield and West Yorkshire, with remote viewing configured on your phone so you can check in from anywhere.",
    included: [
      "HD and 4K camera systems",
      "Discreet, weatherproof camera positioning",
      "Night-vision and motion detection",
      "Remote viewing on phone, tablet or PC",
      "Recording and secure storage set-up",
      "Systems for homes, shops and commercial units",
    ],
    why:
      "We plan every CCTV installation around the areas that matter most to you, run the cabling tidily, and make sure you know exactly how to use your system before we leave. From a single doorway camera to full multi-camera coverage across a business in Leeds, we've got it covered.",
  },
  {
    id: "fire-alarms",
    slug: "fire-alarm-installation",
    num: "03",
    title: "Fire Alarms",
    blurb:
      "Design, installation and testing of interlinked fire alarm and smoke detection systems for domestic and commercial premises.",
    image: img("https://images.pexels.com/photos/103592/pexels-photo-103592.jpeg?cs=tinysrgb&dpr=2", 1100),
    imageAlt: "Fire alarm and smoke detection system on a ceiling",
    seoTitle: "Fire Alarm Installation Bradford | Aims Electrical",
    seoDescription:
      "Design, installation and testing of interlinked fire alarm and smoke detection systems for domestic and commercial premises across West Yorkshire.",
    h1: "Fire Alarm Installation in Bradford",
    intro:
      "Interlinked fire alarms save lives — and for many properties they're now a legal requirement. Aims Electrical designs, installs and tests fire alarm and smoke detection systems for domestic and commercial premises across Bradford and West Yorkshire.",
    included: [
      "Interlinked smoke and heat detectors",
      "Domestic and commercial fire alarm systems",
      "Systems for landlords and HMOs",
      "Fully compliant with current regulations",
      "Testing, servicing and certification",
      "Advice on the right system for your property",
    ],
    why:
      "From a single interlinked system in a family home to a full commercial installation, we'll specify the right solution, fit it neatly and certify it properly — giving you and the people in your building real peace of mind.",
  },
  {
    id: "fault-finding",
    slug: "electrical-fault-finding",
    num: "04",
    title: "Fault Finding & Repairs",
    blurb:
      "Tripping circuits, dead sockets, flickering lights — we diagnose the root cause fast and repair it properly.",
    image: img("https://images.pexels.com/photos/5767595/pexels-photo-5767595.jpeg?cs=tinysrgb&dpr=2", 1100),
    imageAlt: "Electrician diagnosing a fault at a consumer unit",
    seoTitle: "Electrical Fault Finding & Repairs Bradford | Aims Electrical",
    seoDescription:
      "Tripping circuits, dead sockets, flickering lights? We diagnose the root cause fast and repair it properly. Local electrician covering West Yorkshire.",
    h1: "Electrical Fault Finding & Repairs in Bradford",
    intro:
      "Tripping circuits, dead sockets, flickering lights or a burning smell? Electrical faults are frustrating and can be dangerous. Aims Electrical is a local electrician covering Bradford, Halifax, Wakefield and the wider West Yorkshire area, and we find the root cause fast — then repair it properly.",
    included: [
      "Tripping RCDs and circuit breakers",
      "Dead sockets and faulty switches",
      "Flickering or failing lights",
      "Overloaded or damaged circuits",
      "Consumer unit and wiring faults",
      "Full diagnostic testing",
    ],
    why:
      "We don't just fix the symptom. Using proper test equipment we trace the actual fault, explain what's wrong in plain English, and put it right the first time — so the problem stays fixed.",
  },
  {
    id: "emergency",
    slug: "emergency-electrician",
    num: "05",
    title: "24/7 Emergency Callout",
    blurb:
      "Lost power or a dangerous fault out of hours? A qualified electrician is on call around the clock across West Yorkshire.",
    image: img("https://images.unsplash.com/photo-1579114213255-d8d82bfff681?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0", 1100),
    imageAlt: "Emergency electrician responding at night in West Yorkshire",
    seoTitle: "Emergency Electrician Bradford | 24/7 Call-Out | Aims Electrical",
    seoDescription:
      "Lost power or a dangerous electrical fault? A qualified electrician is on call 24/7 across Bradford and West Yorkshire. Call 07907 917 551 now.",
    h1: "24/7 Emergency Electrician in Bradford",
    intro:
      "Lost power, a dangerous fault, or a burning smell out of hours? A qualified emergency electrician is on call 24/7 across Bradford and West Yorkshire. Call 07907 917 551 and we'll get to you as quickly as we can — day or night, weekends and holidays included.",
    included: [
      "Total or partial power loss",
      "Tripping that won't reset",
      "Burning smells or scorched fittings",
      "Fuse board and consumer unit faults",
      "Exposed or damaged wiring",
      "Fast response across West Yorkshire",
    ],
    why:
      "Electrical emergencies don't wait for office hours, and neither do we. You'll speak to a real electrician who can make your property safe quickly — not a call centre.",
  },
];

export const AREAS = [
  "Bradford", "Leeds", "Keighley", "Shipley", "Huddersfield", "Halifax",
  "Wakefield", "Bingley", "Ilkley", "Otley", "Pudsey", "Cleckheaton",
];

export const TESTIMONIALS = [
  { name: "Amaan", quote: "Outstanding service, with a quick responsive time to a call out. Got me back up and running asap." },
  { name: "Zervish", quote: "Highly recommended. Fast service. So glad with the work he has done for me." },
  { name: "Aahil", quote: "Imaad is one of the kindest and hardest working people I've met. I called him for an emergency job and he came as soon as he could to get it fixed. I won't be needing to call anyone else next time." },
  { name: "Sharmini", quote: "Imaad from Aims Electrical completely transformed our extension. His work is top-notch and we couldn't be happier with the results — his attention to detail and high standards truly shine through." },
];

export const GALLERY = [
  { src: img("https://images.unsplash.com/photo-1660330589693-99889d60181e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0", 900), alt: "Electrician installing a new consumer unit in a Bradford home", span: "lg:col-span-7" },
  { src: img("https://images.unsplash.com/photo-1544724569-5f546fd6f2b5?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0", 700), alt: "Consumer unit with neatly labelled circuit breakers", span: "lg:col-span-5" },
  { src: img("https://images.unsplash.com/photo-1704040686413-2c607dbd2f06?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0", 700), alt: "Living room with recessed downlighting installed", span: "lg:col-span-5" },
  { src: img("https://images.pexels.com/photos/5691642/pexels-photo-5691642.jpeg?cs=tinysrgb&dpr=2", 900), alt: "New socket wiring during a house rewire", span: "lg:col-span-7" },
  { src: img("https://images.unsplash.com/photo-1601462904263-f2fa0c851cb9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0", 700), alt: "Neatly terminated cabling in a distribution board", span: "lg:col-span-6" },
  { src: img("https://images.unsplash.com/photo-1589935447067-5531094415d1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0", 700), alt: "CCTV camera installed on a building exterior", span: "lg:col-span-6" },
];

export const HERO_IMAGE = "/gallery/w8.webp";

// Nav uses homepage anchors + dedicated pages. Header resolves hrefs per route.
export const NAV_LINKS = [
  { label: "Services", to: "/#services" },
  { label: "Coverage", to: "/areas-we-cover" },
  { label: "Reviews", to: "/#reviews" },
  { label: "Gallery", to: "/#gallery" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const TOWNS = [
  {
    id: "bradford",
    slug: "electrician-bradford",
    name: "Bradford",
    title: "Electrician in Bradford | Aims Electrical",
    description: "Local, fully-insured electrician in Bradford. Rewires, fuse boards, CCTV, fire alarms, fault finding & 24/7 emergency call-outs. Free quotes — call 07907 917 551.",
    h1: "Electrician in Bradford",
    intro: "Aims Electrical is a local, fully-insured electrician based right here in Bradford. From house rewires and fuse board upgrades to CCTV, fire alarms and 24/7 emergency call-outs, we cover every kind of domestic and commercial electrical work across Bradford and the surrounding area.",
    body: "As a Bradford-based team we know the local housing stock inside out — from older terraces needing a full rewire to new-build extensions. Call us for honest advice and a free, no-obligation quote.",
  },
  {
    id: "leeds",
    slug: "electrician-leeds",
    name: "Leeds",
    title: "Electrician in Leeds | Aims Electrical",
    description: "Reliable electrician covering Leeds & West Yorkshire. Rewires, CCTV, fire alarms, fault finding & 24/7 emergency call-outs. Free quotes — call 07907 917 551.",
    h1: "Electrician in Leeds",
    intro: "Looking for a reliable electrician in Leeds? Aims Electrical provides domestic and commercial electrical services throughout Leeds and West Yorkshire — rewires, CCTV installation, fire alarms, fault finding and round-the-clock emergency call-outs.",
    body: "We're only a short drive from Leeds and work across the city and its suburbs. Whether it's a single socket or a full commercial fit-out, we'll get it done safely and certified.",
  },
  {
    id: "huddersfield",
    slug: "electrician-huddersfield",
    name: "Huddersfield",
    title: "Electrician in Huddersfield | Aims Electrical",
    description: "Local electrician in Huddersfield & Kirklees. Rewires, fuse boards, CCTV, fire alarms, fault finding & 24/7 emergency call-outs. Free quotes — call 07907 917 551.",
    h1: "Electrician in Huddersfield",
    intro: "Aims Electrical is your local electrician for Huddersfield and the wider Kirklees area. We handle rewires, fuse boards, CCTV, fire alarms, fault finding and emergency call-outs for homes and businesses.",
    body: "From Huddersfield town centre to the surrounding villages, we provide prompt, tidy and fully-certified electrical work — with a free quote before we start.",
  },
  {
    id: "halifax",
    slug: "electrician-halifax",
    name: "Halifax",
    title: "Electrician in Halifax | Aims Electrical",
    description: "Electrician in Halifax & Calderdale. Rewires, CCTV, fire alarms, fault finding & 24/7 emergency call-outs. Fully insured, free quotes — call 07907 917 551.",
    h1: "Electrician in Halifax",
    intro: "Need an electrician in Halifax? Aims Electrical covers Halifax and Calderdale for all domestic and commercial electrical work, including rewires, CCTV, fire alarms, fault finding and 24/7 emergency call-outs.",
    body: "We combine fast local response with proper certification and a fixed, up-front price — no surprises.",
  },
  {
    id: "wakefield",
    slug: "electrician-wakefield",
    name: "Wakefield",
    title: "Electrician in Wakefield | Aims Electrical",
    description: "Trusted electrician in Wakefield & West Yorkshire. Rewires, CCTV, fire alarms, fault finding & 24/7 emergency call-outs. Free quotes — call 07907 917 551.",
    h1: "Electrician in Wakefield",
    intro: "Aims Electrical provides trusted electrical services across Wakefield and West Yorkshire — house rewires, fuse board upgrades, CCTV, fire alarms, fault finding and emergency call-outs, for both homes and businesses.",
    body: "Friendly, fully-insured and reliable — call us for a free quote anywhere in the Wakefield area.",
  },
  {
    id: "keighley",
    slug: "electrician-keighley",
    name: "Keighley",
    title: "Electrician in Keighley | Aims Electrical",
    description: "Local electrician in Keighley & the Aire Valley. Rewires, CCTV, fire alarms, fault finding & 24/7 emergency call-outs. Free quotes — call 07907 917 551.",
    h1: "Electrician in Keighley",
    intro: "Looking for an electrician in Keighley? Aims Electrical covers Keighley and the Aire Valley for rewires, CCTV, fire alarms, fault finding, fuse boards and 24/7 emergency call-outs.",
    body: "Local, fully-insured and quick to respond — with all work certified to current regulations.",
  },
];

export const FAQS = [
  { q: "How much does a house rewire cost?", a: "Every property is different, so we give a free, no-obligation fixed quote after a quick assessment — no hidden costs. Factors include the size of the property, the number of circuits and how much of the wiring needs replacing." },
  { q: "Do you offer 24/7 emergency call-outs?", a: "Yes. A qualified emergency electrician is on call around the clock across Bradford and West Yorkshire, including weekends and holidays. Call 07907 917 551 and we'll get to you as quickly as we can." },
  { q: "Are you fully insured?", a: "Yes — Aims Electrical is fully insured with public liability cover, and all work is carried out and certified to the current wiring regulations (BS 7671)." },
  { q: "Which areas do you cover?", a: "We're based in Bradford and cover the whole of West Yorkshire, including Leeds, Huddersfield, Halifax, Wakefield, Keighley, Shipley, Bingley and more. Not sure if we reach you? Just give us a call." },
  { q: "Do you do both domestic and commercial work?", a: "Yes. We work in homes and businesses alike — from a single faulty socket or extra lighting point to full commercial installations, fire alarms and CCTV systems." },
  { q: "Will I get a certificate for the work?", a: "Absolutely. Where applicable you'll receive an Electrical Installation Certificate (or Minor Works Certificate) confirming the work meets current safety regulations." },
];
